import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Check, Menu, X, CheckCircle, User, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import SignupNotification from './components/SignupNotification';
import ScrollReveal from './components/ScrollReveal';
import ContactForm from './components/ContactForm';
import Section from './components/Section';
import ImageFrame from './components/ImageFrame';
import AnimatedBackground from './components/AnimatedBackground';
import FadeInSection from './components/FadeInSection';
import StatCard from './components/StatCard';
import CountUpNumber from './components/CountUpNumber';
import PricingCarousel from './components/PricingCarousel';
import SystemsCarousel from './components/SystemsCarousel';
import LogoCarousel from './components/LogoCarousel';
import ReviewsSection from './components/ReviewsSection';
import VideoPlayer from './components/VideoPlayer';
import useScrollspy from './hooks/useScrollspy';
import { useAuth } from './contexts/AuthContext';
import { supabase } from './lib/supabase';


export default function InstitutionalTradingPlatform() {
  const { isAuthenticated, user } = useAuth();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [openFaq, setOpenFaq] = useState(null);
  const [activePerformanceTab, setActivePerformanceTab] = useState('examples');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [blogExamples, setBlogExamples] = useState([]);
  const [loadingExamples, setLoadingExamples] = useState(true);

  const sectionIds = ['systems', 'tiers', 'performance', 'research', 'about', 'faq', 'contact'];
  const activeSection = useScrollspy(sectionIds);

  // Fetch blog examples 1, 4, 7, 10, 11, 12
  useEffect(() => {
    const fetchBlogExamples = async () => {
      const { data, error } = await supabase
        .from('blog_entries')
        .select('*')
        .in('example_number', [1, 4, 7, 10, 11, 12])
        .order('example_number', { ascending: true });

      if (!error && data) {
        setBlogExamples(data);
      }
      setLoadingExamples(false);
    };

    fetchBlogExamples();
  }, []);

  const logoSrc = '/images/logo-removebg-preview.png';
  // Panels: stronger blue gradient with subtle ring
  const panelWrapperClass =
    'bg-gradient-to-br from-[#0e223d]/90 via-[#1a3e63]/85 to-[#0c2340]/90 border border-[#2a4c73] ring-1 ring-[#2a4c73]/40 overflow-hidden';
  const panelCardClass =
    'bg-gradient-to-br from-[#0c1a30]/95 via-[#152a45]/90 to-[#0a1c33]/95 border border-[#2a4c73]';

  const faqs = [
    {
      question: "How do I get access?",
      answer: [
        "Once you sign up for a plan, you'll receive an email confirmation.",
        "After your account is approved, access to the indicators will be granted directly through TradingView.",
        "If you've subscribed to the Pro or Elite tier, you'll also be given a direct Telegram contact — offering 24/7 access for support and private discussion.",
      ],
    },
    {
      question: "What's coming soon?",
      answer: [
        "We're expanding the HDAlgos ecosystem with:",
        "• New quantitative and regime-based systems",
        "• A private group chat for traders from investment banks, hedge funds, and prop firms, sharing trade ideas and use cases",
        "• Coaching and mentoring on how to trade like an institutional trader — applying the same risk, conviction, and sizing principles used on professional desks",
      ],
    },
    {
      question: "What exactly are these systems?",
      answer: [
        "They're quantitative forecasting and risk-management tools — not buy/sell indicators. Each model quantifies conviction, volatility, and regime conditions so traders can size positions with precision rather than guess direction.",
      ],
    },
    {
      question: "How is the Combined Forecast System calculated?",
      answer: [
        "It merges multiple independent models — breakout, momentum, mean-reversion, volatility, and cross-sectional — into one normalized conviction score (-20 -> +20) with a clear market state: bullish/bearish, strengthening/weakening.",
      ],
    },
    {
      question: "What does the Trend Exhaustion System do?",
      answer: [
        "It measures the statistical lifespan of a trend using survival-analysis techniques.",
        "By tracking age, median exhaustion, and remaining probability, it highlights when momentum is decaying and risk-reward begins to deteriorate.",
      ],
    },
    {
      question: "How does the Regime Detection Engine work?",
      answer: [
        "A Hidden Markov Model classifies markets as trending or choppy based on volatility and return persistence.",
        "It updates in real time and is retrained offline to keep transition probabilities current.",
      ],
    },
    {
      question: "What assets and timeframes can the systems be used on?",
      answer: [
        "They operate on bar-level data, so it makes no difference whether a bar is 1 minute or 1 day.",
        "The framework can run on any liquid asset — equities, futures, FX, crypto, or commodities — adapting automatically to each market's volatility structure.",
      ],
    },
    {
      question: "Where is AI or Machine Learning used?",
      answer: [
        "Machine learning powers the Elite systems — training, validating, and refitting models for each asset and timeframe.",
        "It ensures the framework adapts continuously to changing market structures and volatility regimes.",
      ],
    },
    {
      question: "How often are the models retrained?",
      answer: [
        "Each model follows a rolling offline refit schedule, typically every few weeks or when volatility shifts materially.",
        "This maintains accuracy and prevents parameter drift.",
      ],
    },
    {
      question: "Who built these systems?",
      answer: [
        "Developed by a former Goldman Sachs derivatives trader with an engineering background from a top-10 global university.",
        "Built by someone who has managed institutional risk firsthand, bringing professional discipline to independent trading.",
      ],
    },
  ];

  // Small animated chat preview used on the hero
  const ChatSnippet = () => {
    const userText = 'I want to backtest breakout of daily ATR on a 10m ES1!';
    const intro = 'Sure — for lower timeframes (LTF) in scalp mode, use:';
    const lines = [
      'entry=OpenATRBreakout',
      'use_rth_open=1',
      'atrlen=14',
      'brkmult=0.50',
      'confirm=0',
      'filter=None',
      'stopmult=0.50',
      'tpmult=0.50',
      'usetrail=1',
      'trailmult=0.40',
      'qty=1',
      'flatmins=1',
      'onepersess=0',
    ];

    const [uShown, setUShown] = useState('');
    const [iShown, setIShown] = useState('');
    const [visible, setVisible] = useState(0); // number of preset lines visible
    const [phase, setPhase] = useState('user'); // 'user' -> 'intro' -> 'list' -> 'done'
    const [fadeOut, setFadeOut] = useState(false);

    useEffect(() => {
      let id;
      let t1;
      let t2;
      if (phase === 'user') {
        id = setInterval(() => {
          setUShown(prev => {
            if (prev.length >= userText.length) {
              setPhase('intro');
              return prev;
            }
            return userText.slice(0, prev.length + 1);
          });
        }, 16);
      } else if (phase === 'intro') {
        id = setInterval(() => {
          setIShown(prev => {
            if (prev.length >= intro.length) {
              setPhase('list');
              return prev;
            }
            return intro.slice(0, prev.length + 1);
          });
        }, 14);
      } else if (phase === 'list') {
        id = setInterval(() => {
          setVisible(v => {
            if (v >= lines.length) {
              setPhase('done');
              return v;
            }
            return v + 1;
          });
        }, 120);
      } else if (phase === 'done') {
        // Loop immediately with no pause
        setFadeOut(false);
        // microtask to avoid blocking render
        t1 = setTimeout(() => {
          setUShown('');
          setIShown('');
          setVisible(0);
          setPhase('user');
        }, 0);
      }
      return () => {
        if (id) clearInterval(id);
        if (t1) clearTimeout(t1);
        if (t2) clearTimeout(t2);
      };
    }, [phase]);

    const copy = async () => {
      try { await navigator.clipboard.writeText(lines.map(l => l).join(';')); } catch {}
    };

    return (
      <div className="w-full h-[260px] md:h-[300px] bg-[#0b1526]/80 backdrop-blur-md border border-white/10 rounded-xl overflow-hidden flex flex-col">
        {/* Header like the real chat */}
        <div className="flex items-center justify-between px-3 py-2 bg-white/5 border-b border-white/10">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-semibold text-neutral-300 tracking-wide">AI Chatbot</span>
          </div>
          <div className="flex items-center gap-1 opacity-70">
            <span className="w-1.5 h-1.5 rounded-full bg-neutral-500" />
            <span className="w-1.5 h-1.5 rounded-full bg-neutral-500" />
            <span className="w-1.5 h-1.5 rounded-full bg-neutral-500" />
          </div>
        </div>
        {/* Messages area */}
        <div className="flex-1 p-3 space-y-2 overflow-hidden">
          <div className="flex justify-end">
            <div className="bg-amber-500/90 text-black text-sm px-3 py-2 rounded-lg max-w-[90%] shadow">
              {uShown}
              {phase !== 'intro' && <span className="inline-block w-2 h-4 align-baseline animate-pulse">|</span>}
            </div>
          </div>
          <div className="flex justify-start">
            <div className={`bg-neutral-900/90 text-neutral-100 text-xs md:text-sm px-3 py-2 rounded-lg max-w-[95%] border border-neutral-800 shadow transition-all ${fadeOut ? 'opacity-0 -translate-y-2 duration-700' : 'opacity-100'}`}>
              <div className="mb-1">{iShown}{phase === 'list' && <span className="inline-block w-2 h-4 align-baseline animate-pulse">|</span>}</div>
              <div className="font-mono text-[11px] md:text-xs leading-relaxed">
                {lines.slice(0, visible).map((ln, idx) => (
                  <div key={idx} className="text-emerald-300/90">{ln}</div>
                ))}
              </div>
              {phase === 'done' && (
                <div className="mt-2">
                  <button onClick={copy} className="text-[10px] bg-white/10 hover:bg-white/20 text-white px-2 py-1 rounded border border-white/10">Copy all</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // AI Preset promo under hero (blended into hero)
  const AIPresetPromo = () => (
    <section className="relative z-10 w-full pt-6 pb-4 bg-transparent">
      <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8 items-center px-4">
        <div>
          <h3 className="text-3xl md:text-4xl font-black leading-tight mb-3">
            <span className="text-white">A Complete </span>
            <span className="gradient-text">Configurable Backtest</span>
            <span className="text-white"> on TradingView</span>
          </h3>
          <p className="text-neutral-300 mb-6">Use the AI Chatbot to set presets for entries, filters, stops, trailing and risk — describe your idea, paste the preset, and backtest instantly.</p>
          <Link to="/ai-chatbot" className="inline-flex items-center gap-2 bg-amber-500 text-black px-6 py-3 rounded-lg font-bold hover:bg-amber-400 transition">
            Try the AI Chatbot
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
        <ChatSnippet />
      </div>
    </section>
  );

  const systems = [
    {
      id: 'trend-exhaustion',
      navLabel: 'Trend Exhaustion',
      title: 'Trend Exhaustion System',
      subtitle: 'Measuring the Life Cycle of a Trend',
      description:
        'Every trend has a lifespan. This system tells you how far through it you are — and how much strength remains.',
      metrics: [
        { label: 'Age & Median', desc: 'Duration vs. norm' },
        { label: 'Survival %', desc: 'Continuation probability' },
        { label: 'Quartile', desc: 'Early/Mid/Late' },
        { label: 'Bars Left', desc: 'Expected life' },
      ],
      cta: {
        href: '/system-library#trend-exhaustion',
        label: 'View full methodology',
      },
      wrapperClass: `${panelWrapperClass} p-6 md:p-8`,
      layout: {
        textColumnClass: 'md:basis-[45%] lg:max-w-2xl',
        imageColumnClass: 'md:basis-[55%] lg:max-w-3xl',
        textFirst: true, // Text on left, image on right (AlgoPro style)
      },
      images: [
        {
          id: 'te-new',
          label: 'Chart View',
          src: '/images/este.png',
          alt: 'Trend Exhaustion Engine chart view',
          caption:
            'Real-time survival analysis showing trend life cycle position',
        },
        {
          id: 'te',
          label: 'Dashboard View',
          src: '/images/te.png',
          alt: 'Trend exhaustion dashboard view',
          caption: 'Trend exhaustion dashboard overview',
        },
      ],
      defaultImageId: 'te-new',
    },
    {
      id: 'combined-forecast',
      navLabel: 'Forecast Paint Engine',
      title: 'Forecast Paint Engine',
      subtitle: 'One Unified Conviction Signal',
      description:
        'Multiple quantitative forecasts combined into one conviction framework, visualised directly on price through adaptive bar colouring.',
      cta: {
        href: '/system-library#combined-forecast',
        label: 'View full methodology',
      },
      wrapperClass: `${panelWrapperClass} p-6 md:p-8`,
      layout: {
        textColumnClass: 'md:basis-[55%] lg:max-w-3xl',
        imageColumnClass: 'md:basis-[45%] lg:max-w-2xl',
        textFirst: true, // Text on left, image on right (AlgoPro style)
      },
      images: [
        {
          id: 'forecast1',
          label: 'Chart View',
          src: '/images/paintmain2.png',
          alt: 'Combined forecast paint engine',
          caption: 'Unified conviction score with market state classification',
        },
        {
          id: 'forecasts',
          label: 'Dashboard View',
          src: '/images/forecasts.png',
          alt: 'Combined forecast dashboard view',
          caption: 'Combined forecast dashboard showing all systems',
        },
      ],
      defaultImageId: 'forecast1',
      marketStatesLabel: 'The Four Market States',
      marketStates: [
        {
          id: 'bullish-strengthening',
          label: 'Bullish Strengthening',
          desc: 'Accelerating',
          accentClass: 'text-green-500',
          activeClass: 'bg-green-950/30 ring-2 ring-green-500/50',
          inactiveClass:
            'bg-neutral-900/50 ring-1 ring-neutral-800/50 hover:ring-[#CBB26A]/30',
        },
        {
          id: 'bullish-weakening',
          label: 'Bullish Weakening',
          desc: 'Losing energy',
          accentClass: 'text-green-400',
          activeClass: 'bg-green-950/20 ring-2 ring-green-400/50',
          inactiveClass:
            'bg-neutral-900/50 ring-1 ring-neutral-800/50 hover:ring-[#CBB26A]/30',
        },
        {
          id: 'bearish-strengthening',
          label: 'Bearish Strengthening',
          desc: 'Intensifying',
          accentClass: 'text-red-500',
          activeClass: 'bg-red-950/30 ring-2 ring-red-500/50',
          inactiveClass:
            'bg-neutral-900/50 ring-1 ring-neutral-800/50 hover:ring-[#CBB26A]/30',
        },
        {
          id: 'bearish-weakening',
          label: 'Bearish Weakening',
          desc: 'Base forming',
          accentClass: 'text-red-400',
          activeClass: 'bg-red-950/20 ring-2 ring-red-400/50',
          inactiveClass:
            'bg-neutral-900/50 ring-1 ring-neutral-800/50 hover:ring-[#CBB26A]/30',
        },
      ],
    },
    {
      id: 'volatility-breakout',
      navLabel: 'Volatility Breakout',
      title: 'Volatility Breakout System',
      subtitle: 'Capturing Explosive Moves',
      description:
        'Identifies high-probability breakout conditions by analyzing volatility expansion, price compression, and momentum acceleration.',
      metrics: [
        { label: 'Compression', desc: 'Range contraction' },
        { label: 'Volatility Spike', desc: 'Expansion trigger' },
        { label: 'Momentum', desc: 'Follow-through strength' },
        { label: 'Breakout Quality', desc: 'Conviction score' },
      ],
      cta: {
        href: '/system-library#volatility-breakout',
        label: 'View full methodology',
      },
      wrapperClass: `${panelWrapperClass} p-6 md:p-8`,
      layout: {
        textColumnClass: 'md:basis-[50%]',
        imageColumnClass: 'md:basis-[50%]',
        textFirst: true, // Text on left, image on right (AlgoPro style)
      },
      images: [
        {
          id: 'vb-new',
          label: 'Chart View',
          src: '/images/esbo.png',
          alt: 'Volatility Breakout Engine',
          caption: 'Volatility compression and breakout detection',
        },
        {
          id: 'vb2',
          label: 'Dashboard View',
          src: '/images/VBO.png',
          alt: 'Volatility breakout dashboard',
          caption: 'Breakout analysis dashboard',
        },
      ],
      defaultImageId: 'vb-new',
    },
    {
      id: 'swing-scalp-bot',
      navLabel: 'Swing / Scalp Bot',
      title: 'Swing / Scalp Bot',
      subtitle: 'Mode‑adaptive execution maps',
      description:
        'Crystal‑clear entries, stops and multi‑target exits painted directly on price, with conviction‑weighted sizing for Scalp or Swing modes.',
      cta: {
        href: '/system-library#hdalgo-pro-engine',
        label: 'View full methodology',
      },
      wrapperClass: `${panelWrapperClass} p-6 md:p-8`,
      layout: {
        textColumnClass: 'md:basis-[55%] lg:max-w-3xl',
        imageColumnClass: 'md:basis-[45%] lg:max-w-2xl',
        textFirst: true,
      },
      images: [
        { id: 'eng1', label: 'Dashboard', src: '/images/ESengine.png', alt: 'Swing / Scalp Bot dashboard', caption: 'Trade map with targets and risk' },
        { id: 'eng2', label: 'Chart', src: '/images/engine1.png', alt: 'Swing / Scalp Bot chart view', caption: 'Entries, stops and targets on chart' },
        { id: 'eng3', label: 'Paint', src: '/images/pe1.png', alt: 'Swing / Scalp Bot paint', caption: 'Conviction paint and structure' },
      ],
      defaultImageId: 'eng1',
    },
    {
      id: 'regime-detection',
      navLabel: 'Regime Detection',
      title: 'Regime Detection Engine',
      subtitle: 'Trending or Choppy?',
      description:
        'Two-state Hidden Markov Model classifying markets as Trend or Chop — optimizing strategy selection in real-time.',
      metrics: [
        { label: 'Current State', desc: 'Trend or Chop' },
        { label: 'Probability', desc: 'Confidence level' },
        { label: 'Transitions', desc: 'Regime shifts' },
        { label: 'Duration', desc: 'Time in state' },
      ],
      cta: {
        href: '/system-library#regime-detection',
        label: 'View full methodology',
      },
      wrapperClass: `${panelWrapperClass} p-6 md:p-8`,
      layout: {
        textColumnClass: 'md:basis-[45%] lg:max-w-2xl',
        imageColumnClass: 'md:basis-[55%] lg:max-w-3xl',
        textFirst: true, // Text on left, image on right (AlgoPro style)
      },
      images: [
        {
          id: 'regime',
          label: 'Dashboard View',
          src: '/images/regime.png',
          alt: 'Regime detection dashboard view',
          caption:
            'Real-time regime classification with transition probabilities',
        },
        {
          id: 'te1',
          label: 'Chart View',
          src: '/images/te1.png',
          alt: 'Regime detection chart view',
          caption: 'Regime detection integrated with trend exhaustion',
        },
      ],
      defaultImageId: 'regime',
    },
    {
      id: 'conditions-dashboard',
      navLabel: 'Conditions Dashboard',
      title: 'Conditions Dashboard',
      subtitle: 'Situational Awareness Layer',
      description:
        'Transforms multi-indicator chaos into one coherent market state — aggregating behavior across models and timeframes.',
      metrics: [
        { label: 'Indicator States', desc: 'EMA, volatility, ADX' },
        { label: 'Timeframe Sync', desc: 'Multi-horizon' },
        { label: 'State Labels', desc: 'Bull/Bear Continue' },
        { label: 'Conflict Detection', desc: 'Divergence alerts' },
      ],
      cta: {
        href: '/system-library#conditions-dashboard',
        label: 'View full methodology',
      },
      wrapperClass: `${panelWrapperClass} p-6 md:p-8`,
      layout: {
        textColumnClass: 'md:basis-[55%] lg:max-w-3xl',
        imageColumnClass: 'md:basis-[45%] lg:max-w-2xl',
        textFirst: true, // Text on left, image on right (AlgoPro style)
      },
      images: [
        {
          id: 'state',
          label: 'Chart',
          src: '/images/state.png',
          alt: 'Conditions dashboard chart view',
          caption:
            'State detection overlay showing multi-timeframe alignment',
        },
        {
          id: 'state1',
          label: 'Dashboard 1',
          src: '/images/state1.png',
          alt: 'Conditions dashboard detail view',
          caption: 'Indicator state table with momentum condition analysis',
        },
        {
          id: 'state2',
          label: 'Dashboard 2',
          src: '/images/state2.png',
          alt: 'Conditions dashboard timeframe alignment view',
          caption: 'Timeframe alignment dashboard with directional bias',
        },
      ],
      defaultImageId: 'state',
    },
    {
      id: 'pca-sensor',
      navLabel: 'PCA Sensor',
      title: 'PCA Sensor',
      subtitle: 'Factor vs. Residual Analysis',
      description:
        'Isolates true market drivers by decomposing returns into systemic factors and asset-specific deviations.',
      metrics: [
        { label: 'Relative-Value', desc: 'Cluster deviations' },
        { label: 'Cross-Asset', desc: 'Factor vs residual' },
        { label: 'Correlation', desc: 'Dispersion spikes' },
        { label: 'Mean Reversion', desc: 'Regime-filtered' },
      ],
      cta: {
        href: '/system-library#pca-sensor',
        label: 'View full methodology',
      },
      wrapperClass: `${panelWrapperClass} p-6 md:p-8`,
      layout: {
        textColumnClass: 'md:basis-[50%] lg:max-w-2xl',
        imageColumnClass: 'md:basis-[50%] lg:max-w-3xl',
        textFirst: true, // Text on left, image on right (AlgoPro style)
      },
      images: [
        {
          id: 'pca1',
          label: 'Dashboard View',
          src: '/images/pca1.png',
          alt: 'PCA sensor view',
          caption:
            'PCA residual analysis showing factor vs idiosyncratic returns',
        },
      ],
      defaultImageId: 'pca1',
    },
    {
      id: 'relative-trend',
      navLabel: 'Relative Trend',
      title: 'Relative Trend System',
      subtitle: 'Cross-Asset Strength Analysis',
      description:
        'Measures relative performance and trend strength across correlated assets to identify leadership, divergence, and rotation patterns.',
      metrics: [
        { label: 'Relative Strength', desc: 'vs benchmark' },
        { label: 'Trend Quality', desc: 'Consistency score' },
        { label: 'Divergence', desc: 'Rotation signals' },
        { label: 'Leadership', desc: 'Market positioning' },
      ],
      cta: {
        href: '/system-library#relative-trend',
        label: 'View full methodology',
      },
      wrapperClass: `${panelWrapperClass} p-6 md:p-8`,
      layout: {
        textColumnClass: 'md:basis-[50%] lg:max-w-2xl',
        imageColumnClass: 'md:basis-[50%] lg:max-w-3xl',
        textFirst: true, // Text on left, image on right (AlgoPro style)
      },
      images: [
        {
          id: 'rt1',
          label: 'Chart View',
          src: '/images/reltrendvol.png',
          alt: 'Relative trend system',
          caption: 'Cross-asset relative strength and trend analysis',
        },
        {
          id: 'rt2',
          label: 'Table View',
          src: '/images/reltrendvoltable.png',
          alt: 'Relative trend table',
          caption: 'Relative trend comparison table',
        },
      ],
      defaultImageId: 'rt1',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0c1a30] via-[#152a45] to-[#0a1c33] text-white scroll-smooth overflow-x-hidden">
      {/* Signup Notifications */}
      <SignupNotification />

      {/* Navigation - Enhanced Dark Style */}
  <nav className="fixed top-0 w-full bg-gradient-to-br from-[#0e223d] via-[#1a3e63] to-[#0c2340] backdrop-blur-xl border-b border-[#2a4c73] z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <div className="flex items-center">
              <img src={logoSrc} alt="HDAlgos logo" className="h-8 sm:h-10 w-auto" />
              <span className="sr-only">HDAlgos Institutional Systems</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              <a href="/system-library" className="text-sm text-gray-400 hover:text-[#CBB26A] transition">Indicator Library</a>
              <a href="/ai-ml" className="text-sm text-gray-400 hover:text-[#CBB26A] transition">AI / Machine Learning</a>
              <a href="/ai-chatbot" className="text-sm text-gray-400 hover:text-[#CBB26A] transition">AI Chatbot</a>
              <a href="/blog" className="text-sm text-gray-400 hover:text-[#CBB26A] transition">Analysis Examples</a>
              <a href="/trade-ideas" className="text-sm text-gray-400 hover:text-[#CBB26A] transition">Trade Ideas</a>
              <a href="#tiers" className={`text-sm transition ${activeSection === 'tiers' ? 'text-[#CBB26A] font-semibold' : 'text-gray-400 hover:text-[#CBB26A]'}`}>Pricing</a>
              <a href="#performance" className={`text-sm transition ${activeSection === 'performance' ? 'text-[#CBB26A] font-semibold' : 'text-gray-400 hover:text-[#CBB26A]'}`}>Performance</a>
              <a href="#faq" className={`text-sm transition ${activeSection === 'faq' ? 'text-[#CBB26A] font-semibold' : 'text-gray-400 hover:text-[#CBB26A]'}`}>FAQ</a>

              {isAuthenticated ? (
                <Link to="/account" className="flex items-center gap-2 bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-black px-6 py-3 rounded-lg font-bold text-sm hover:from-[#D8C585] hover:to-[#BDA45E] transition">
                  <User className="w-4 h-4" />
                  {user?.fullName || 'Account'}
                </Link>
              ) : (
                <div className="flex items-center gap-3">
                  <Link to="/login" className="text-sm text-gray-400 hover:text-white transition font-semibold">
                    Sign In
                  </Link>
                  <Link to="/register" className="bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-black px-6 py-3 rounded-lg font-bold text-sm hover:from-[#D8C585] hover:to-[#BDA45E] transition">
                    Get Started
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-3 text-gray-400 hover:text-white transition rounded-lg hover:bg-white/5"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#0a0a0a]/95 border-t border-white/10">
            <div className="px-6 py-6 space-y-2">
              <a href="/system-library" className="block py-4 px-4 text-base text-gray-400 hover:text-[#CBB26A] hover:bg-white/5 rounded-lg transition" onClick={() => setMobileMenuOpen(false)}>Indicator Library</a>
              <a href="/ai-ml" className="block py-4 px-4 text-base text-gray-400 hover:text-[#CBB26A] hover:bg-white/5 rounded-lg transition" onClick={() => setMobileMenuOpen(false)}>AI / Machine Learning</a>
              <a href="/ai-chatbot" className="block py-4 px-4 text-base text-gray-400 hover:text-[#CBB26A] hover:bg-white/5 rounded-lg transition" onClick={() => setMobileMenuOpen(false)}>AI Chatbot</a>
              <a href="/blog" className="block py-4 px-4 text-base text-gray-400 hover:text-[#CBB26A] hover:bg-white/5 rounded-lg transition" onClick={() => setMobileMenuOpen(false)}>Analysis Examples</a>
              <a href="/trade-ideas" className="block py-4 px-4 text-base text-gray-400 hover:text-[#CBB26A] hover:bg-white/5 rounded-lg transition" onClick={() => setMobileMenuOpen(false)}>Trade Ideas</a>
              <a href="#tiers" className="block py-4 px-4 text-base text-gray-400 hover:text-[#CBB26A] hover:bg-white/5 rounded-lg transition" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
              <a href="#performance" className="block py-4 px-4 text-base text-gray-400 hover:text-[#CBB26A] hover:bg-white/5 rounded-lg transition" onClick={() => setMobileMenuOpen(false)}>Performance</a>
              <a href="#faq" className="block py-4 px-4 text-base text-gray-400 hover:text-[#CBB26A] hover:bg-white/5 rounded-lg transition" onClick={() => setMobileMenuOpen(false)}>FAQ</a>

              {isAuthenticated ? (
                <Link to="/account" className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-black px-6 py-5 rounded-lg font-bold text-base hover:from-[#D8C585] hover:to-[#BDA45E] transition mt-4" onClick={() => setMobileMenuOpen(false)}>
                  <User className="w-5 h-5" />
                  {user?.fullName || 'Account'}
                </Link>
              ) : (
                <div className="space-y-2 mt-4">
                  <Link to="/login" className="block w-full text-center bg-white/5 ring-1 ring-white/10 text-white px-6 py-4 rounded-lg font-bold text-base hover:ring-[#CBB26A]/50 hover:text-[#CBB26A] transition" onClick={() => setMobileMenuOpen(false)}>
                    Sign In
                  </Link>
                  <Link to="/register" className="block w-full text-center bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-black px-6 py-5 rounded-lg font-bold text-base hover:from-[#D8C585] hover:to-[#BDA45E] transition" onClick={() => setMobileMenuOpen(false)}>
                    Get Started
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - Enhanced Blue Gradient */}
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden">
        {/* Prominent dark blue gradient background for hero section */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0e223d] via-[#1a3e63] to-[#0c2340] backdrop-blur-xl border-b border-[#2a4c73]">
          <div className="absolute top-[-10%] left-1/5 w-[900px] h-[900px] bg-[#153050]/45 rounded-full blur-[180px]" />
          <div className="absolute bottom-[-10%] right-1/6 w-[900px] h-[900px] bg-[#122a47]/40 rounded-full blur-[180px]" />
          <div className="absolute top-1/2 left-1/2 w-[700px] h-[700px] bg-[#CBB26A]/14 rounded-full blur-[180px] -translate-x-1/2 -translate-y-1/2" />
        </div>
        <AnimatedBackground />

        <div className="max-w-7xl mx-auto relative z-10 px-6">
          <div className="text-center mb-16 md:mb-20">
            <FadeInSection>
              <span className="inline-block px-4 py-2 rounded-full bg-[#CBB26A]/10 border border-[#CBB26A]/20 text-[#CBB26A] text-sm font-medium mb-8">
                Powered by institutional-grade algorithms
              </span>
            </FadeInSection>

            <FadeInSection delay={0.1}>
              <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold leading-[1.1] mb-8 tracking-tight">
                <span className="text-white">Trade With </span>
                <span className="bg-gradient-to-r from-[#CBB26A] via-[#D8C585] to-[#A8914D] bg-clip-text text-transparent font-black">Institutional Edge</span>
              </h1>
            </FadeInSection>

            <FadeInSection delay={0.2}>
              <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
                TradingView indicators developed by a former{' '}
                <span className="bg-gradient-to-r from-[#CBB26A] to-[#D8C585] bg-clip-text text-transparent font-bold">Goldman Sachs Trader</span>.
                Quantify conviction. Control exposure. Trade with precision.
              </p>
            </FadeInSection>

            <FadeInSection delay={0.3}>
              <div className="flex justify-center items-center mb-16">
                <a
                  href="#tiers"
                  className="flex items-center justify-center bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-black px-8 py-4 rounded-xl font-bold text-lg hover:from-[#D8C585] hover:to-[#BDA45E] transition-all shadow-lg shadow-[#CBB26A]/25 hover:shadow-[#CBB26A]/40"
                >
                  Get Started
                </a>
              </div>
            </FadeInSection>

            {/* Stats - Zayalana style */}
            <FadeInSection delay={0.4}>
              <div className="grid grid-cols-3 gap-8 max-w-3xl mx-auto">
                <div className="text-center">
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#CBB26A] via-[#D8C585] to-[#A8914D] bg-clip-text text-transparent mb-2">
                    <CountUpNumber end={1000} suffix="+" />
                  </div>
                  <div className="text-sm text-gray-400">Active Traders</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#CBB26A] via-[#D8C585] to-[#A8914D] bg-clip-text text-transparent mb-2">
                    <CountUpNumber end={40} suffix="+" />
                  </div>
                  <div className="text-sm text-gray-400">Years Data Validated</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#CBB26A] via-[#D8C585] to-[#A8914D] bg-clip-text text-transparent mb-2">
                    <CountUpNumber end={18} />
                  </div>
                  <div className="text-sm text-gray-400">Independent Systems</div>
                </div>
              </div>
            </FadeInSection>
            {/* Scalp/Swing Bot Video Section */}
            <FadeInSection delay={0.5}>
              <div className="flex flex-col items-center mt-12">
                <div className="flex flex-col items-center mb-4">
                  <span className="text-lg md:text-xl font-bold text-white mb-2">See our Scalp / Swing bot</span>
                  <span className="animate-bounce text-3xl text-[#CBB26A]">↓</span>
                </div>
                <div className="w-full max-w-5xl rounded-xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50">
                  <VideoPlayer
                    src="/images/HDAlgosEngine-ES-vo.mp4"
                    className="w-full h-full object-cover"
                    autoPlay={false}
                    enableMobileExpand={true}
                  />
                </div>
              </div>
            </FadeInSection>
          </div>
        </div>
        {/* AI preset promo blended into hero */}
        <AIPresetPromo />
      </section>

      {/* Logo Carousel */}
      <LogoCarousel />

      {/* Zayalana-Style Explanation Section */}
      <section className="w-full py-16 md:py-24 bg-gradient-to-br from-[#0a1628] via-[#132238] to-[#061220]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <FadeInSection>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              <span className="text-white">How Professional Traders</span><br />
              <span className="gradient-text">Gain Their Edge</span>
            </h2>
          </FadeInSection>
          <FadeInSection delay={0.1}>
            <p className="text-lg md:text-xl text-gray-400 leading-relaxed max-w-3xl mx-auto">
              Whether you're scalping intraday moves or building swing positions, our institutional-grade systems transform complex market data into actionable intelligence. See how top traders use our platform to quantify conviction, manage exposure, and execute with precision before the competition.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* Next-Gen Algorithms Section */}
      <section className="w-full py-2">
        <div className="w-full bg-gradient-to-br from-[#0e223d] via-[#1a3e63] to-[#0c2340] rounded-2xl border border-[#2a4c73] ring-1 ring-[#2a4c73]/40">
          <div className="grid lg:grid-cols-[45%_55%] gap-6 md:gap-8 items-center min-h-[640px]">
            {/* Text - Left on desktop */}
            <div className="order-2 lg:order-1 p-8 md:p-12 lg:p-16 flex flex-col justify-center">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-black leading-tight mb-4">
                <span className="text-white">Next-Gen </span>
                <span className="gradient-text">Algorithms</span>
              </h2>
              
              <p className="text-lg md:text-xl text-white font-semibold mb-6">
                Trade with confidence by spotting high-probability opportunities before everyone else
              </p>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#CBB26A] flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-[#CBB26A] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Cut through the noise. Our indicators turn messy charts into clean levels, trend bias, VWAP zones, and smart alerts—so you instantly know where price is likely to react. No clutter, no guesswork, just clarity.
                  </p>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#CBB26A] flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-[#CBB26A] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Dial it in to your style in seconds. Choose your market, timeframe, and sensitivity to match how you trade—scalps, swings, futures, crypto, indices. The visuals stay clean and readable, even on busy days.
                  </p>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#CBB26A] flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-[#CBB26A] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    The result? Fewer impulse trades, more high-quality entries. Traders choose HDAlgos because seeing the right information at the right time changes outcomes. Add the indicators, and feel the confidence boost on your next session.
                  </p>
                </div>
              </div>
              
              <Link 
                to="/system-library" 
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-white px-6 py-3 rounded-full font-bold hover:from-[#D8C585] hover:to-[#BDA45E] transition-all w-fit"
              >
                Learn more about our indicators
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            
            {/* Media - Right on desktop (match style of other panels) */}
            <div className="order-1 lg:order-2">
              <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.3 }}>
                <img
                  src="/images/Screenshot 2025-12-05 at 21.16.23.png"
                  alt="Next‑Gen Algorithms"
                  className="w-full h-auto rounded-lg shadow-2xl border border-white/10"
                  onError={(e)=>{ e.currentTarget.onerror=null; e.currentTarget.src='/images/Screenshot 2025-12-05 at 20.29.00.png'; }}
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Swing / Scalp Bot */}
      <section className="w-full py-2">
        <div className="w-full bg-gradient-to-br from-[#0e223d] via-[#1a3e63] to-[#0c2340] rounded-2xl border border-[#2a4c73] ring-1 ring-[#2a4c73]/40">
          <div className="grid lg:grid-cols-2 gap-6 md:gap-8 items-center min-h-[520px] p-6 sm:p-8 md:p-10 lg:p-12">
            {/* Text - Left on desktop */}
            <div className="order-2 lg:order-1 lg:pr-8 text-left">
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-black leading-tight mb-4">
                <span className="text-white">Swing / Scalp </span>
                <span className="gradient-text">Bot</span>
              </h2>

              <p className="text-lg md:text-xl text-white font-semibold mb-6">
                Trade maps, not numbers — the Bot paints entries, stops and multi‑targets directly on price and adapts instantly between Scalp and Swing modes.
                Built‑in backtesting mirrors live execution (multi‑targets, break‑even, trailing and session filters) so your stats match how you actually trade.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#CBB26A] flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-[#CBB26A] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Mode selector (Scalp / Swing) tunes sensitivity, targets and trailing behaviour instantly without cluttering the chart.
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#CBB26A] flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-[#CBB26A] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Realistic backtesting: multi‑targets, break‑even logic, session filters and intrabar‑aware modelling for execution‑grade stats.
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#CBB26A] flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-[#CBB26A] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Conviction paint + structure overlays provide clear, repeatable execution maps across assets and timeframes.
                  </p>
                </div>
              </div>
            </div>

            {/* Media - Right on desktop */}
            <div className="order-1 lg:order-2">
              <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.3 }}>
                <img
                  src="/images/ESengine.png"
                  alt="Swing / Scalp Bot"
                  className="w-full h-auto rounded-lg shadow-2xl border border-white/10"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* HDAlgos Sniper (moved directly under Swing / Scalp Bot) */}
      <section className="w-full py-2">
        <div className="w-full bg-gradient-to-br from-[#0e223d] via-[#1a3e63] to-[#0c2340] rounded-2xl border border-[#2a4c73] ring-1 ring-[#2a4c73]/40">
          <div className="grid lg:grid-cols-2 gap-6 md:gap-8 items-center min-h-[500px] p-6 sm:p-8 md:p-10 lg:p-12">
            {/* Text - Left on desktop */}
            <div className="order-2 lg:order-1 lg:pr-8 text-left">
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-black leading-tight mb-4">
                <span className="text-white">HDAlgos </span>
                <span className="gradient-text">Sniper</span>
              </h2>

              <p className="text-lg md:text-xl text-white font-semibold mb-6">
                An all‑in‑one backtesting strategy: entries, exits, scaling, and risk — validated with realistic rules and automation‑ready outputs
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#CBB26A] flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-[#CBB26A] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Designed meticulously for low‑timeframe futures price action, with clean confluence for high‑probability setups.
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#CBB26A] flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-[#CBB26A] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Ultra‑fast scalping capabilities with session‑based customisability for real trading schedules.
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#CBB26A] flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-[#CBB26A] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    DCA and martingale options for precise contract management, plus comprehensive risk controls suited to prop‑firm constraints.
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#CBB26A] flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-[#CBB26A] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Built for futures but adaptable to any liquid market — with webhook signals ready for 3rd‑party automation.
                  </p>
                </div>
              </div>
            </div>

            {/* Media - Right on desktop */}
            <div className="order-1 lg:order-2">
              <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.3 }}>
                <img
                  src="/images/Screenshot 2025-12-05 at 20.04.20.png"
                  alt="HDAlgos Sniper"
                  className="w-full h-auto rounded-lg shadow-2xl border border-white/10"
                  onError={(e)=>{ e.currentTarget.onerror=null; e.currentTarget.src='/images/custom-strategy.png'; }}
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* HDAlgos Trend Exhaustion Engine */}
      <section className="w-full py-2">
        <div className="w-full bg-gradient-to-br from-[#0e223d] via-[#1a3e63] to-[#0c2340] rounded-2xl border border-[#2a4c73] ring-1 ring-[#2a4c73]/40">
          <div className="grid lg:grid-cols-2 gap-6 md:gap-8 items-center min-h-[500px] p-6 sm:p-8 md:p-10 lg:p-12">
            {/* Text - Left on desktop */}
            <div className="order-2 lg:order-1 lg:pr-8 text-left">
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-black leading-tight mb-4">
                <span className="text-white">HDAlgos Trend Exhaustion </span>
                <span className="gradient-text">Engine</span>
              </h2>

              <p className="text-lg md:text-xl text-white font-semibold mb-6">
                Know when to hold — and when the trend is statistically running out of steam
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#CBB26A] flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-[#CBB26A] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Quantifies trend maturity using survival analysis — see whether a move is early, mid, or late stage relative to its historical life cycle.
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#CBB26A] flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-[#CBB26A] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Highlights rising exhaustion risk as the trend approaches median lifespan — reduce size proactively as continuation probability decays.
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#CBB26A] flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-[#CBB26A] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Combine with the Forecast Paint Engine for conviction-weighted entries/exits and objective risk adjustments.
                  </p>
                </div>
              </div>
            </div>

            {/* Media - Right on desktop */}
            <div className="order-1 lg:order-2">
              <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.3 }}>
                <img
                  src="/images/este.png"
                  alt="HDAlgos Trend Exhaustion Engine"
                  className="w-full h-auto rounded-lg shadow-2xl border border-white/10"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* HDAlgos Volatility Breakout Engine */}
      <section className="w-full py-2">
        <div className="w-full bg-gradient-to-br from-[#0e223d] via-[#1a3e63] to-[#0c2340] rounded-2xl border border-[#2a4c73] ring-1 ring-[#2a4c73]/40">
          <div className="grid lg:grid-cols-2 gap-6 md:gap-8 items-center min-h-[500px] p-6 sm:p-8 md:p-10 lg:p-12">
            {/* Text - Left on desktop */}
            <div className="order-2 lg:order-1 lg:pr-8 text-left">
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-black leading-tight mb-4">
                <span className="text-white">HDAlgos Volatility Breakout </span>
                <span className="gradient-text">Engine</span>
              </h2>

              <p className="text-lg md:text-xl text-white font-semibold mb-6">
                Detect compression, time expansion — and ride the move with context
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#CBB26A] flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-[#CBB26A] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Identifies volatility compression and release with structure-aware signals — spot clean breakouts and early trend phases.
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#CBB26A] flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-[#CBB26A] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Reads retests versus weakening structure — repeated tests at the same level often signal fading momentum.
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#CBB26A] flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-[#CBB26A] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Uses regime and volatility context for sizing — high-vol breakouts often travel further but snap back faster.
                  </p>
                </div>
              </div>
            </div>

            {/* Media - Right on desktop */}
            <div className="order-1 lg:order-2">
              <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.3 }}>
                <img
                  src="/images/esbo.png"
                  alt="HDAlgos Volatility Breakout Engine"
                  className="w-full h-auto rounded-lg shadow-2xl border border-white/10"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>


      {/* AutoPatternDetection */}
      <section className="w-full py-2">
        <div className="w-full bg-gradient-to-br from-[#0e223d] via-[#1a3e63] to-[#0c2340] rounded-2xl border border-[#2a4c73] ring-1 ring-[#2a4c73]/40">
          <div className="grid lg:grid-cols-2 gap-6 md:gap-8 items-center min-h-[500px] p-6 sm:p-8 md:p-10 lg:p-12">
            {/* Text - Left on desktop */}
            <div className="order-2 lg:order-1 lg:pr-8 text-left">
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-black leading-tight mb-4">
                <span className="text-white">AutoPattern</span>
                <span className="gradient-text">Detection</span>
              </h2>

              <p className="text-lg md:text-xl text-white font-semibold mb-6">
                Automated pattern recognition with probabilistic forecasting and precise entry/exit levels
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#CBB26A] flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-[#CBB26A] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Instantly detects classic chart patterns including rising/falling wedges, triangles, flags, pennants, and inside/outside bars with statistical confidence scores.
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#CBB26A] flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-[#CBB26A] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Each pattern comes with probability-weighted directional forecasts, helping you understand the likelihood of continuation vs. reversal before committing capital.
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#CBB26A] flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-[#CBB26A] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Automatically calculates optimal entry zones, take-profit targets, and stop-loss levels based on pattern geometry and historical performance — removing guesswork from execution.
                  </p>
                </div>
              </div>
            </div>

            {/* Media - Right on desktop */}
            <div className="order-1 lg:order-2">
              <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.3 }}>
                <div className="bg-neutral-900/80 border border-neutral-800 p-2 md:p-3 rounded-xl">
                  <img
                    src="/images/patterndetec.png"
                    alt="AutoPatternDetection example"
                    className="w-full h-auto rounded-lg shadow-2xl border border-white/10"
                    loading="lazy"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      

      {/* Forecast Paint Engine Section - AlgoPro Style */}
      <section className="w-full py-2">
        <div className="w-full bg-gradient-to-br from-[#0e223d] via-[#1a3e63] to-[#0c2340] rounded-2xl border border-[#2a4c73] ring-1 ring-[#2a4c73]/40">
          <div className="grid grid-cols-1 md:grid-cols-2 min-h-[500px]">
            {/* Left: Text Content */}
            <div className="p-8 md:p-12 lg:p-16 flex flex-col justify-center">
              <p className="text-[#CBB26A] text-sm uppercase tracking-widest mb-4">Visual Intelligence</p>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-black leading-tight mb-4">
                <span className="gradient-text">Forecast Paint Engine</span>
              </h2>
              
              <p className="text-lg md:text-xl text-white font-semibold mb-6">
                Transform quantitative forecasts into intuitive visuals — market statistics become colour and structure
              </p>
              
              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#CBB26A] flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-[#CBB26A] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Every bar represents a synthesis of momentum, volatility, and directional bias. The colour palette shifts to reflect the underlying forecast—green hues for bullish conviction, red for bearish, and muted tones when the signal is neutral.
                  </p>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#CBB26A] flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-[#CBB26A] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Interpret complex data at a glance — no manual decoding, no endless numbers. The visual language is designed to communicate strength, direction, and confidence instantly, so you can make decisions faster.
                  </p>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 border-[#CBB26A] flex items-center justify-center mt-1">
                    <div className="w-2 h-2 bg-[#CBB26A] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed">
                    Immediate context — strength, conviction, and change, painted directly on the chart. No need to switch between panels or interpret separate indicators. Everything you need is embedded in the price action itself.
                  </p>
                </div>
              </div>
              
              <Link 
                to="/system-library" 
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-white px-6 py-3 rounded-full font-bold hover:from-[#D8C585] hover:to-[#BDA45E] transition-all w-fit"
              >
                Explore the Paint Engine
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            
            {/* Right: Image */}
            <div className="flex items-center justify-center p-4 md:p-8">
              <motion.div
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
                className="w-full h-full rounded-xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50"
              >
                <img
                  src="/images/paintmain copy 2.png"
                  alt="Forecast Paint Engine"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="w-full bg-gradient-to-br from-[#0a1628] via-[#132238] to-[#061220]">
        <div className="max-w-4xl mx-auto text-center px-6 pt-16 pb-8">
          <FadeInSection>
            <p className="text-[#CBB26A] text-sm uppercase tracking-widest mb-4">Testimonials</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              <span className="text-white">Trusted By </span>
              <span className="gradient-text">Professional Traders</span>
            </h2>
            <p className="text-gray-400 text-lg md:text-xl leading-relaxed">
              Join hundreds of traders who've upgraded their edge with our institutional-grade systems. 
              See what our community has to say about their experience.
            </p>
          </FadeInSection>
        </div>
        <ReviewsSection />
      </section>

      {/* Systems Library Section */}
      <section className="w-full bg-gradient-to-br from-[#0a1628] via-[#132238] to-[#061220]">
        <div className="max-w-4xl mx-auto text-center px-6 pt-16 pb-8">
          <FadeInSection>
            <p className="text-[#CBB26A] text-sm uppercase tracking-widest mb-4">Indicator Suite</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              <span className="text-white">Explore Our </span>
              <span className="gradient-text">Trading Systems</span>
            </h2>
            <p className="text-gray-400 text-lg md:text-xl leading-relaxed">
              Each system is engineered to solve a specific problem — from measuring trend exhaustion to detecting regime changes. 
              Explore our full suite of indicators and find the perfect toolkit for your trading style.
            </p>
          </FadeInSection>
        </div>
        <SystemsCarousel systems={systems} />
      </section>

      {/* Pricing Section */}
      <section id="tiers" className="w-full bg-gradient-to-br from-[#0a1628] via-[#132238] to-[#061220]">
        <div className="max-w-4xl mx-auto text-center px-6 pt-16 pb-8">
          <FadeInSection>
            <p className="text-[#CBB26A] text-sm uppercase tracking-widest mb-4">Flexible Plans</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              <span className="text-white">Investment That </span>
              <span className="gradient-text">Pays For Itself</span>
            </h2>
            <p className="text-gray-400 text-lg md:text-xl leading-relaxed">
              Choose the tier that matches your trading goals. From foundation tools to full institutional intelligence, 
              every plan includes access to our proven systems and dedicated support.
            </p>
          </FadeInSection>
        </div>

        <div className="max-w-7xl mx-auto px-6 pb-12">
          <ScrollReveal>
            <div className="text-center mb-10 md:mb-12">
              {/* Founder Access Tagline */}
              <div className="mb-12 md:mb-16 max-w-4xl mx-auto px-4">
                <div className="gradient-border p-8 md:p-10">
                  <p className="text-lg sm:text-xl md:text-2xl text-[#CBB26A] font-bold mb-4 md:mb-5">
                    All Members Get Exclusive Access
                  </p>
                  <p className="text-base sm:text-lg md:text-xl text-gray-300 leading-relaxed">
                    24/7 direct messaging with the developer — a former Goldman Sachs derivatives trader with institutional-level expertise
                  </p>
                </div>
              </div>
            </div>
          </ScrollReveal>

          <PricingCarousel billingCycle={billingCycle} />

          {/* Legacy hardcoded pricing (removed) */}
          <div className="hidden">
          {/*
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            {/* CORE TIER */}
            <div className="bg-neutral-950 ring-1 ring-neutral-800 hover:ring-[#CBB26A]/50 p-8 rounded-xl shadow-lg transition h-full flex flex-col">
              <div className="mb-8">
                <div className="text-3xl font-black mb-2">CORE</div>
                <div className="text-xs font-bold tracking-widest uppercase text-[#CBB26A] mb-4">Foundation Toolkit</div>

                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-5xl font-black">${billingCycle === 'monthly' ? '49' : '39'}</span>
                  <span className="text-neutral-500 text-sm">/ mo</span>
                </div>
                {billingCycle === 'yearly' && (
                  <div className="text-sm text-[#CBB26A]">Billed annually at $470</div>
                )}
              </div>

              <div className="space-y-3 mb-8 text-sm flex-grow">
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#CBB26A] flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-400">Forecast Paint Engine (Flagship)</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#CBB26A] flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-400">EMAC ML Adaptive System (Coming Soon)</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#CBB26A] flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-400">Cross-Sectional Relative Momentum</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#CBB26A] flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-400">Directional Probability System</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#CBB26A] flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-400">Intraday Volatility Timezone System</span>
                </div>
              </div>

              <Link to="/checkout?plan=core" className="w-full bg-neutral-900 ring-1 ring-neutral-700 text-white py-4 rounded-lg font-bold hover:ring-[#CBB26A] hover:text-[#CBB26A] transition mt-auto block text-center">
                SELECT CORE
              </Link>
            </div>

            {/* PRO TIER */}
            <div className="bg-neutral-950 ring-2 ring-[#CBB26A] p-8 rounded-xl relative transform lg:scale-105 shadow-2xl shadow-[#CBB26A]/10 h-full flex flex-col">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-black px-6 py-2 rounded-full font-black text-xs tracking-wide">
                MOST POPULAR
              </div>

              <div className="mb-8">
                <div className="text-3xl font-black mb-2">PRO</div>
                <div className="text-xs font-bold tracking-widest uppercase text-[#CBB26A] mb-4">Advanced Systems</div>

                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-5xl font-black">${billingCycle === 'monthly' ? '89' : '70'}</span>
                  <span className="text-neutral-500 text-sm">/ mo</span>
                </div>
                {billingCycle === 'yearly' && (
                  <div className="text-sm text-[#CBB26A]">Billed annually at $850</div>
                )}
              </div>

              <div className="space-y-3 mb-8 text-sm flex-grow">
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#CBB26A] flex-shrink-0 mt-0.5" />
                  <span className="text-[#CBB26A] font-bold">Everything in CORE, plus:</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#CBB26A] flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-400">Trend Exhaustion System (Flagship)</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#CBB26A] flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-400">Volatility Breakout System</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#CBB26A] flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-400">Relative Trend System</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#CBB26A] flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-400">Conditions Dashboard</span>
                </div>
              </div>

              <Link to="/checkout?plan=pro" className="w-full bg-gradient-to-r from-[#CBB26A] to-[#A8914D] text-black py-4 rounded-lg font-black hover:from-[#D8C585] hover:to-[#BDA45E] transition mt-auto block text-center">
                SELECT PRO
              </Link>
            </div>

            {/* ELITE TIER */}
            <div className="bg-neutral-950 ring-1 ring-neutral-800 hover:ring-[#CBB26A]/50 p-8 rounded-xl shadow-lg transition relative h-full flex flex-col">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-red-600 text-white px-6 py-2 rounded-full font-black text-sm">
                SOLD OUT
              </div>

              <div className="mb-8">
                <div className="text-3xl font-black mb-2">ELITE</div>
                <div className="text-xs font-bold tracking-widest uppercase text-[#CBB26A] mb-4">Institutional Intelligence</div>

                <div className="flex items-baseline gap-2 mb-2 opacity-50">
                  <span className="text-5xl font-black line-through">${billingCycle === 'monthly' ? '150' : '120'}</span>
                  <span className="text-neutral-500 text-sm">/ mo</span>
                </div>
                {billingCycle === 'yearly' && (
                  <div className="text-sm text-[#CBB26A] opacity-50 line-through">Billed annually at $1,440</div>
                )}
              </div>

              <div className="space-y-3 mb-8 text-sm flex-grow">
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#CBB26A] flex-shrink-0 mt-0.5" />
                  <span className="text-[#CBB26A] font-bold">Everything in PRO, plus:</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#CBB26A] flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-400">Regime Detection Engine</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#CBB26A] flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-400">PCA Sensor</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#CBB26A] flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-400">Leader/Follower Network</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#CBB26A] flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-400">ML Regime/Covariance</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#CBB26A] flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-400">Research Library</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#CBB26A] flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-400">Python/Excel Exports</span>
                </div>
              </div>

              <button className="w-full bg-[#CBB26A]/20 ring-1 ring-[#CBB26A] text-[#CBB26A] py-4 rounded-lg font-bold hover:bg-[#CBB26A]/30 transition mt-auto">
                JOIN WAITING LIST
              </button>
            </div>
          </div>
          */}
          {/* PLATINUM TIER removed as requested */}
        </div>
      </section>

      {/* Performance Section */}
      <section id="performance" className="w-full bg-gradient-to-br from-[#0a1628] via-[#132238] to-[#061220]">
        <div className="max-w-4xl mx-auto text-center px-6 py-12">
          <FadeInSection>
            <p className="text-[#CBB26A] text-sm uppercase tracking-widest mb-4">Real Results</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              <span className="text-white">Systems In </span>
              <span className="gradient-text">Action</span>
            </h2>
            <p className="text-gray-400 text-lg md:text-xl leading-relaxed">
              Don't take our word for it — see the systems applied to real market conditions. Explore examples 
              across multiple asset classes and timeframes to understand how our indicators guide conviction and execution.
            </p>
          </FadeInSection>
        </div>

        <div className="max-w-5xl mx-auto px-6 pb-12">
          <FadeInSection>
            <div className={`${panelWrapperClass} p-6 md:p-8`}>
              {/* Tabs */}
              <div className="flex justify-center gap-2 mb-10 flex-wrap">
                <button
                  onClick={() => setActivePerformanceTab('examples')}
                  className={`px-6 py-3 rounded-lg font-bold text-sm transition ${
                    activePerformanceTab === 'examples'
                      ? 'bg-[#CBB26A] text-black'
                      : 'bg-neutral-900 ring-1 ring-neutral-800 text-neutral-400 hover:text-white'
                  }`}
                >
                  Examples
                </button>
                <button
                  onClick={() => setActivePerformanceTab('nikkei')}
                  className={`px-6 py-3 rounded-lg font-bold text-sm transition ${
                    activePerformanceTab === 'nikkei'
                      ? 'bg-[#CBB26A] text-black'
                      : 'bg-neutral-900 ring-1 ring-neutral-800 text-neutral-400 hover:text-white'
                  }`}
                >
                  Nikkei 1H
                </button>
                <button
                onClick={() => setActivePerformanceTab('gold')}
                className={`px-6 py-3 rounded-lg font-bold text-sm transition ${
                  activePerformanceTab === 'gold'
                    ? 'bg-[#CBB26A] text-black'
                    : 'bg-neutral-900 ring-1 ring-neutral-800 text-neutral-400 hover:text-white'
                }`}
              >
                Gold 1H
              </button>
              <button
                onClick={() => setActivePerformanceTab('gas')}
                className={`px-6 py-3 rounded-lg font-bold text-sm transition ${
                  activePerformanceTab === 'gas'
                    ? 'bg-[#CBB26A] text-black'
                    : 'bg-neutral-900 ring-1 ring-neutral-800 text-neutral-400 hover:text-white'
                }`}
              >
                Crude Oil 1H
              </button>
              <button
                onClick={() => setActivePerformanceTab('crude')}
                className={`px-6 py-3 rounded-lg font-bold text-sm transition ${
                  activePerformanceTab === 'crude'
                    ? 'bg-[#CBB26A] text-black'
                    : 'bg-neutral-900 ring-1 ring-neutral-800 text-neutral-400 hover:text-white'
                }`}
              >
                Natural Gas 1H
              </button>
              <button
                onClick={() => setActivePerformanceTab('silver')}
                className={`px-6 py-3 rounded-lg font-bold text-sm transition ${
                  activePerformanceTab === 'silver'
                    ? 'bg-[#CBB26A] text-black'
                    : 'bg-neutral-900 ring-1 ring-neutral-800 text-neutral-400 hover:text-white'
                }`}
              >
                Silver 10m
              </button>
              <button
                onClick={() => setActivePerformanceTab('nasdaq')}
                className={`px-6 py-3 rounded-lg font-bold text-sm transition ${
                  activePerformanceTab === 'nasdaq'
                    ? 'bg-[#CBB26A] text-black'
                    : 'bg-neutral-900 ring-1 ring-neutral-800 text-neutral-400 hover:text-white'
                }`}
              >
                Nasdaq 10m
              </button>
            </div>

            {/* Tab Content */}
            {activePerformanceTab === 'examples' && (
              <div className="space-y-6">
                {loadingExamples ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#CBB26A]"></div>
                  </div>
                ) : blogExamples.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-neutral-400">No examples available yet.</p>
                  </div>
                ) : (
                  blogExamples.map((example) => {
                    const firstImage = example.image_urls?.[0] || example.image_url;
                    const truncatedContent = example.content?.length > 200
                      ? example.content.substring(0, 200) + '...'
                      : example.content;

                    return (
                      <div key={example.id} className="bg-neutral-900/50 border border-neutral-800 rounded-lg p-6 hover:border-neutral-700 transition">
                        <div className="flex flex-col lg:flex-row gap-6">
                          {/* Image Preview */}
                          {firstImage && (
                            <div className="lg:w-1/3 flex-shrink-0">
                              <img
                                src={firstImage}
                                alt={example.title}
                                className="w-full h-48 lg:h-full object-cover rounded-lg"
                              />
                            </div>
                          )}

                          {/* Content */}
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <div className="text-sm text-[#CBB26A] font-bold mb-1">
                                  EXAMPLE #{example.example_number}
                                </div>
                                <h3 className="text-xl font-bold text-white mb-2">
                                  {example.title}
                                </h3>
                                <div className="text-sm text-neutral-500">
                                  {new Date(example.date).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </div>
                              </div>
                            </div>

                            <p className="text-neutral-300 text-sm leading-relaxed mb-4 whitespace-pre-wrap">
                              {truncatedContent}
                            </p>

                            {example.content?.length > 200 && (
                              <Link
                                to="/blog"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-[#CBB26A] text-black font-bold rounded-lg hover:bg-[#A8914D] transition text-sm"
                              >
                                Read more
                                <ChevronRight className="w-4 h-4" />
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
            {activePerformanceTab === 'nikkei' && (
              <ImageFrame
                src="/images/nik.png"
                alt="Nikkei trade execution"
                caption="Nikkei futures conviction trade managed through system signals"
              />
            )}
            {activePerformanceTab === 'gold' && (
              <ImageFrame
                src="/images/gold.png"
                alt="Gold trade walkthrough"
                caption="Gold breakout captured with volatility-targeted sizing"
              />
            )}
            {activePerformanceTab === 'gas' && (
              <ImageFrame
                src="/images/gas.png"
                alt="Natural gas position monitoring"
                caption="Natural gas trade management during regime shift"
              />
            )}
            {activePerformanceTab === 'crude' && (
              <ImageFrame
                src="/images/crude.png"
                alt="Crude oil trade report"
                caption="Crude oil short captured via combined forecast signals"
              />
            )}
            {activePerformanceTab === 'silver' && (
              <ImageFrame
                src="/images/silver10m.png"
                alt="Silver trade walkthrough"
                caption="Silver 10m setup sized with intraday conviction signals"
              />
            )}
            {activePerformanceTab === 'nasdaq' && (
              <ImageFrame
                src="/images/nasdaq10m.png"
                alt="Nasdaq trade walkthrough"
                caption="Nasdaq 10m example showing rapid rotation capture"
              />
            )}
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="w-full bg-gradient-to-br from-[#0a1628] via-[#132238] to-[#061220]">
        <div className="max-w-4xl mx-auto text-center px-6 py-12">
          <FadeInSection>
            <p className="text-[#CBB26A] text-sm uppercase tracking-widest mb-4">Our Story</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              <span className="text-white">Institutional Experience, </span>
              <span className="gradient-text">Independent Access</span>
            </h2>
            <p className="text-gray-400 text-lg md:text-xl leading-relaxed">
              These systems weren't built by marketers — they were engineered by someone who's traded on professional desks, 
              managed real risk, and understands how institutions measure conviction.
            </p>
          </FadeInSection>
        </div>

        <div className="max-w-4xl mx-auto px-6 pb-12">
          <FadeInSection>
            <div className={`${panelWrapperClass} p-8 md:p-10`}>
              <div className="space-y-6 text-base md:text-lg text-neutral-300 leading-relaxed">
                <p className="text-sm font-bold text-[#CBB26A] tracking-widest uppercase">
                  Former Goldman Sachs Derivatives Trader
                </p>
                <p>
                  Built by a former Goldman Sachs derivatives trader with an engineering background from a top-10 global university. These systems are grounded in the same principles used on professional trading desks — conviction-based sizing, volatility targeting, and regime-aware risk control.
                </p>

                <div className={`${panelCardClass} p-6`}>
                  <p className="text-base md:text-lg text-white font-bold leading-relaxed">
                    "Most retail indicators are built by marketers. These were engineered by someone who's sat on a trading desk, executed and risk-managed real positions, and understands how professionals actually measure conviction."
                  </p>
                </div>
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="w-full bg-gradient-to-br from-[#0a1628] via-[#132238] to-[#061220]">
        <div className="max-w-4xl mx-auto text-center px-6 py-12">
          <FadeInSection>
            <p className="text-[#CBB26A] text-sm uppercase tracking-widest mb-4">Support</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              <span className="text-white">Questions? </span>
              <span className="gradient-text">We've Got Answers</span>
            </h2>
            <p className="text-gray-400 text-lg md:text-xl leading-relaxed">
              Everything you need to know about our systems, methodology, and how to get started.
            </p>
          </FadeInSection>
        </div>

        <div className="max-w-4xl mx-auto px-6 pb-12 space-y-4">
          {faqs.map((faq, index) => (
            <FadeInSection key={faq.question} delay={index * 0.05}>
              <motion.div
                whileHover={{ scale: 1.01 }}
                className={`${panelCardClass} p-6 transition-all hover:ring-[#CBB26A]/40`}
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between text-left gap-4 group"
                >
                  <span className="text-base md:text-lg font-bold text-white group-hover:text-[#CBB26A] transition">{faq.question}</span>
                  <motion.div
                    animate={{ rotate: openFaq === index ? 90 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronRight className="w-5 h-5 text-[#CBB26A] flex-shrink-0" />
                  </motion.div>
                </button>
                <motion.div
                  initial={false}
                  animate={{
                    height: openFaq === index ? 'auto' : 0,
                    opacity: openFaq === index ? 1 : 0,
                  }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  {openFaq === index && (
                    <div className="mt-4 pt-4 border-t border-neutral-800 space-y-2 text-sm text-neutral-400 leading-relaxed">
                      {faq.answer.map((paragraph, idx) => (
                        <p key={idx}>{paragraph}</p>
                      ))}
                    </div>
                  )}
                </motion.div>
              </motion.div>
            </FadeInSection>
          ))}
        </div>
      </section>

      {/* Contact Us Section */}
      <section id="contact" className="w-full bg-gradient-to-br from-[#0a1628] via-[#132238] to-[#061220]">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className={`${panelWrapperClass} p-8 md:p-10 lg:p-12`}>
            <ScrollReveal>
              <div className="text-center mb-10">
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight mb-6">
                  Get In{' '}
                  <span className="gradient-text">Touch</span>
                </h2>
                <p className="text-lg md:text-xl text-neutral-400 max-w-3xl mx-auto leading-relaxed">
                  Have questions about our systems? Want to discuss custom solutions? We're here to help.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={200}>
              <ContactForm />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-neutral-900 py-16 px-6 bg-[#111315]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center mb-4">
                <img src={logoSrc} alt="HDAlgos logo" className="h-8 w-auto" />
                <span className="sr-only">HDAlgos Institutional Systems</span>
              </div>
              <p className="text-sm text-neutral-600">
                Institutional-grade quantitative systems for serious professionals
              </p>
            </div>
            <div>
              <div className="text-xs font-bold tracking-wider mb-4 text-neutral-500 uppercase">Product</div>
              <ul className="space-y-2 text-sm text-neutral-600">
                <li><a href="#systems" className="hover:text-white transition">Indicators</a></li>
                <li><a href="#pricing" className="hover:text-white transition">Pricing</a></li>
                <li><a href="#performance" className="hover:text-white transition">Research</a></li>
              </ul>
            </div>
            <div>
              <div className="text-xs font-bold tracking-wider mb-4 text-neutral-500 uppercase">Resources</div>
              <ul className="space-y-2 text-sm text-neutral-600">
                <li><a href="#systems" className="hover:text-white transition">Documentation</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition">Methodology</a></li>
                <li><a href="#contact" className="hover:text-white transition">API Access</a></li>
              </ul>
            </div>
            <div>
              <div className="text-xs font-bold tracking-wider mb-4 text-neutral-500 uppercase">Legal</div>
              <ul className="space-y-2 text-sm text-neutral-600">
                <li><a href="#contact" className="hover:text-white transition">Terms of Service</a></li>
                <li><a href="#contact" className="hover:text-white transition">Risk Disclaimer</a></li>
                <li><a href="#contact" className="hover:text-white transition">Privacy Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-neutral-900 pt-8 text-center">
            <p className="text-sm text-neutral-500 mb-4">© 2025 HDAlgos Systems. All rights reserved.</p>
            <div className="max-w-5xl mx-auto space-y-3 text-xs text-neutral-500 leading-relaxed">
              <p>
                Trading and investing are risky, and many will lose money in connection with trading and investing activities. All
                content on this site is not intended to, and should not be, construed as financial advice. Decisions to buy, sell,
                hold or trade in securities, commodities, and other investments involve risk and are best made based on the advice
                of qualified financial professionals. Past performance does not guarantee future results.
              </p>
              <p>
                Hypothetical or simulated performance results have certain limitations. Unlike an actual performance record,
                simulated results do not represent actual trading and may under-or-over compensate for the impact of market factors
                such as liquidity. No representation is being made that any account will or is likely to achieve profit or losses
                similar to those shown.
              </p>
              <p>
                Testimonials appearing on this website may not be representative of other clients or customers and are not a
                guarantee of future performance or success. As a provider of technical analysis tools and strategies, HDAlgos does
                not have access to the personal trading accounts or brokerage statements of our customers and therefore has no
                reason to believe our customers perform better or worse than traders as a whole based on any content, tool, or
                platform feature we provide.
              </p>
              <p>
                Charts used on this site are by TradingView, where the majority of our technical indicators are built. TradingView®
                is a registered trademark of TradingView, Inc. www.TradingView.com. TradingView® has no affiliation with the owner,
                developer, or provider of the services described herein.
              </p>
              <p>
                Market data is provided by CBOE, CME Group, BarChart, and CoinAPI. All data is provided “as is” and should be
                verified independently for trading purposes.
              </p>
              <p>
                This does not represent our full disclaimer.{' '}
                <Link to="/disclaimer" className="text-[#CBB26A] hover:text-[#A8914D] underline">
                  Read our full disclaimer here.
                </Link>
              </p>
              <p className="pt-1">
                Our site is for educational use only and should not be considered financial advice. Trading and investing activities
                are inherently risky, and users of our material should be prepared to incur losses in connection with such
                activities.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
